import { fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import type {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from "@reduxjs/toolkit/query";
import { getToken, removeToken } from "../utils/auth";
import ApiEndpoints from "./apiEndPoints";
import { toast } from "./toast";

// Create base query instance
const createBaseQuery = fetchBaseQuery({
  baseUrl: ApiEndpoints.baseApiURL,
  prepareHeaders: (headers) => {
    const token = getToken();
    if (token) headers.set("authorization", `Bearer ${token}`);
    headers.set("content-type", "application/json");
    return headers;
  },
});

// Helper to get a user-friendly label from an endpoint name
const getEntityLabel = (endpointName: string) => {
  if (!endpointName) return "";
  
  const specialEndpoints: Record<string, string> = {
    login: "Login",
    logout: "Logout",
    forgotPassword: "Forgot Password request",
    resetPassword: "Password reset",
  };

  if (specialEndpoints[endpointName]) return specialEndpoints[endpointName];

  const match = endpointName.match(/^(?:create|edit|delete|toggle|update|add|remove|assign|unassign|set)(.+)$/i);
  if (match && match[1]) {
    let label = match[1];
    // Remove common suffixes that don't help the label
    label = label.replace(/ById$/i, "");
    label = label.replace(/Status$/i, "");
    // Add spaces before capitals and trim
    label = label.replace(/([A-Z])/g, ' $1').trim();
    return label;
  }
  
  return "";
};

export const baseQueryWithAuth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const result = await createBaseQuery(args, api, extraOptions);

  // Check if the request is a mutation
  const method = typeof args === 'string' ? 'GET' : args.method || 'GET';
  const isMutation = ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method.toUpperCase());

  // Handle global response messages based on status codes
  const hideToast = (extraOptions as any)?.hideToast;

  if (result.error && !hideToast) {
    const errorData: any = result.error.data;
    const serverMessage = errorData?.message || (typeof errorData === 'string' ? errorData : "");
    const status = result.error.status;

    switch (status) {
      case 400:
        toast.error(serverMessage || "Invalid request. Please check your data.");
        break;
      case 401:
        toast.error(serverMessage || "Session expired or unauthorized. Please log in.");
        break;
      case 403:
        toast.error(serverMessage || "You do not have permission for this action.");
        break;
      case 404:
        toast.error(serverMessage || "The requested resource was not found.");
        break;
      case 409:
        toast.error(serverMessage || "A conflict occurred (e.g., resource already exists).");
        break;
      case 422:
        toast.error(serverMessage || "Validation failed. Please check your input fields.");
        break;
      case 500:
      case 502:
      case 503:
      case 504:
        toast.error(serverMessage || "Server error. Please try again later.");
        break;
      case "FETCH_ERROR":
        toast.error("Network error. Please check your connection.");
        break;
      case "PARSING_ERROR":
        toast.error("Error parsing the server response.");
        break;
      case "TIMEOUT_ERROR":
        toast.error("The request timed out. Please try again.");
        break;
      case "CUSTOM_ERROR":
        toast.error("An unexpected error occurred.");
        break;
      default:
        toast.error(serverMessage || `An error occurred. Status: ${status}`);
        break;
    }
  } else if (isMutation && result.data && !hideToast) {
    // Handle global success messages ONLY for mutations
    const responseData: any = result.data;
    
    // Try to extract a name or title from the response result
    const entity = responseData?.result || (Array.isArray(responseData?.results) ? responseData.results[0] : responseData?.results) || responseData;
    const displayName = entity?.name || entity?.title || entity?.username || entity?.firstName || "";
    
    const entityLabel = getEntityLabel(api.endpoint);
    const prefix = entityLabel ? `${entityLabel} ` : "";
    const status = result.meta?.response?.status;
    const methodUpper = method.toUpperCase();

    // Special case for login
    if (api.endpoint === 'login' && status === 200) {
      toast.success(displayName ? `Successfully logged in as ${displayName}.` : "Successfully logged in.");
      return result;
    }

    switch (status) {
      case 200:
        if (methodUpper === 'POST') {
          toast.success(displayName ? `Successfully created ${prefix}${displayName}.` : `Successfully created ${entityLabel || 'record'}.`);
        } else if (methodUpper === 'DELETE') {
          toast.success(displayName ? `Successfully deleted ${prefix}${displayName}.` : `Successfully deleted ${entityLabel || 'record'}.`);
        } else {
          toast.success(displayName ? `Successfully updated ${prefix}${displayName}.` : `Successfully updated ${entityLabel || 'record'}.`);
        }
        break;
      case 201:
        toast.success(displayName ? `Successfully created ${prefix}${displayName}.` : `Successfully created ${entityLabel || 'record'}.`);
        break;
      case 202:
        toast.success("Request accepted and is processing.");
        break;
      case 204:
        if (methodUpper === 'DELETE') {
          toast.success(entityLabel ? `${entityLabel} deleted successfully.` : "Record deleted successfully.");
        } else {
          toast.success(entityLabel ? `${entityLabel} updated successfully.` : "Action completed successfully.");
        }
        break;
      default:
        toast.success(displayName ? `Action successful for ${prefix}${displayName}.` : `${entityLabel || 'Action'} successful.`);
        break;
    }
  }


  // Handle 401 Unauthorized
  if (result.error && result.error.status === 401) {
    // Clear authentication data
    removeToken();
    localStorage.clear();
    sessionStorage.clear();

    // Redirect to login, include the page we came from
    const from = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    window.location.href = `/login?from=${encodeURIComponent(from)}`;
  }

  return result;
};
